import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'

import {
    Check,
    LoaderCircle,
    RotateCcw,
    Send
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { Magnetic } from '@/components/motion/magnetic'
import { CONTACT_LIMITS, CONTACT_TOPICS, EMAIL_PATTERN } from '@/constants/contact'
import { EASE_OUT } from '@/constants/motion'

type FieldName = 'name' | 'email' | 'message'
type FormErrors = Partial<Record<FieldName, string>>
type Status = 'idle' | 'sending' | 'success' | 'error'

interface FormValues {
    name: string
    email: string
    topic: string
    message: string
    company: string
}

const INITIAL_VALUES: FormValues = {
    name: '',
    email: '',
    topic: CONTACT_TOPICS[0],
    message: '',
    company: ''
}

const FIELD_ORDER: FieldName[] = ['name', 'email', 'message']

const validate = (values: FormValues): FormErrors => {
    const errors: FormErrors = {}
    const name = values.name.trim()
    const email = values.email.trim()
    const message = values.message.trim()

    if (!name) errors.name = 'Please tell me your name.'
    else if (name.length > CONTACT_LIMITS.name) errors.name = `Please keep your name under ${CONTACT_LIMITS.name} characters.`

    if (!email) errors.email = 'Please add your email so I can reply.'
    else if (email.length > CONTACT_LIMITS.email || !EMAIL_PATTERN.test(email)) errors.email = 'This email address does not look right.'

    if (message.length < CONTACT_LIMITS.messageMin) errors.message = 'Your message is a little short, tell me a bit more.'
    else if (message.length > CONTACT_LIMITS.messageMax) errors.message = `Please keep your message under ${CONTACT_LIMITS.messageMax} characters.`

    return errors
}

const inputClass = (invalid: boolean): string => cn(
    'w-full rounded-2xl border-2 bg-cream px-4 py-3 text-base text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-secondary focus:bg-white',
    invalid ? 'border-destructive' : 'border-transparent'
)

export const ContactForm: FC = (): ReactNode => {
    const formRef = useRef<HTMLFormElement>(null)
    const [values, setValues] = useState<FormValues>(INITIAL_VALUES)
    const [errors, setErrors] = useState<FormErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [serverError, setServerError] = useState('')

    const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target

        setValues(current => ({ ...current, [name]: value }))

        if (name in errors) {
            setErrors(current => ({ ...current, [name]: undefined }))
        }
    }

    const focusField = (field: FieldName) => {
        formRef.current?.querySelector<HTMLElement>(`[name='${field}']`)?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'sending') return

        const nextErrors = validate(values)

        setErrors(nextErrors)

        const firstInvalid = FIELD_ORDER.find(field => nextErrors[field])

        if (firstInvalid) {
            focusField(firstInvalid)
            return
        }

        setStatus('sending')
        setServerError('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const data = await response.json().catch(() => null) as { message?: string, errors?: FormErrors } | null

            if (!response.ok) {
                if (data?.errors) setErrors(data.errors)

                setServerError(data?.message ?? 'Your message could not be sent right now. Please try again in a moment.')
                setStatus('error')
                return
            }

            setValues(INITIAL_VALUES)
            setStatus('success')
        }
        catch {
            setServerError('Your message could not be sent. Please check your connection and try again.')
            setStatus('error')
        }
    }

    const reset = () => {
        setStatus('idle')
        setServerError('')
        setErrors({})
    }

    return (
        <div className='relative overflow-hidden rounded-[2rem] bg-background p-6 text-foreground shadow-[0_40px_90px_-40px_rgba(20,20,20,0.55)] sm:p-8 md:p-10'>
            <AnimatePresence mode='wait' initial={false}>
                {status === 'success' ? (
                    <motion.div
                        key='success'
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -24 }}
                        transition={{ duration: 0.5, ease: EASE_OUT }}
                        className='flex min-h-[420px] flex-col items-start justify-center'
                        role='status'
                    >
                        <motion.span
                            initial={{ scale: 0, rotate: -30 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ type: 'spring', stiffness: 260, damping: 15, delay: 0.1 }}
                            className='flex size-16 items-center justify-center rounded-full bg-secondary text-white'
                        >
                            <Check aria-hidden='true' className='size-8' />
                        </motion.span>

                        <h3 className='mt-6 font-heading text-4xl font-extrabold tracking-tight'>
                            Message sent!
                        </h3>

                        <p className='mt-3 max-w-sm text-lg leading-relaxed text-muted-foreground'>
                            Thanks for reaching out. I read every message and I will get back to you very soon.
                        </p>

                        <button
                            type='button'
                            onClick={reset}
                            className='mt-8 inline-flex items-center gap-2 rounded-full border-2 border-foreground px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.14em] transition-colors hover:bg-foreground hover:text-background'
                        >
                            <RotateCcw aria-hidden='true' className='size-4' />
                            Send another message
                        </button>
                    </motion.div>
                ) : (
                    <motion.form
                        key='form'
                        ref={formRef}
                        onSubmit={handleSubmit}
                        noValidate
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -24 }}
                        transition={{ duration: 0.5, ease: EASE_OUT }}
                        className='flex flex-col gap-5'
                        aria-describedby={serverError ? 'contact-server-error' : undefined}
                    >
                        <div className='grid gap-5 sm:grid-cols-2'>
                            <div>
                                <label htmlFor='contact-name' className='mb-2 block font-mono text-xs font-bold uppercase tracking-[0.14em]'>
                                    Your name
                                </label>

                                <input
                                    id='contact-name'
                                    name='name'
                                    type='text'
                                    autoComplete='name'
                                    value={values.name}
                                    onChange={handleChange}
                                    maxLength={CONTACT_LIMITS.name}
                                    aria-invalid={Boolean(errors.name)}
                                    aria-describedby={errors.name ? 'contact-name-error' : undefined}
                                    className={inputClass(Boolean(errors.name))}
                                />

                                {errors.name && (
                                    <p id='contact-name-error' className='mt-2 text-sm font-medium text-destructive'>
                                        {errors.name}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label htmlFor='contact-email' className='mb-2 block font-mono text-xs font-bold uppercase tracking-[0.14em]'>
                                    Your email
                                </label>

                                <input
                                    id='contact-email'
                                    name='email'
                                    type='email'
                                    autoComplete='email'
                                    inputMode='email'
                                    value={values.email}
                                    onChange={handleChange}
                                    maxLength={CONTACT_LIMITS.email}
                                    aria-invalid={Boolean(errors.email)}
                                    aria-describedby={errors.email ? 'contact-email-error' : undefined}
                                    className={inputClass(Boolean(errors.email))}
                                />

                                {errors.email && (
                                    <p id='contact-email-error' className='mt-2 text-sm font-medium text-destructive'>
                                        {errors.email}
                                    </p>
                                )}
                            </div>
                        </div>

                        <fieldset>
                            <legend className='mb-3 block font-mono text-xs font-bold uppercase tracking-[0.14em]'>
                                What is it about?
                            </legend>

                            <div className='flex flex-wrap gap-2'>
                                {CONTACT_TOPICS.map(topic => (
                                    <label key={topic} className='cursor-pointer'>
                                        <input
                                            type='radio'
                                            name='topic'
                                            value={topic}
                                            checked={values.topic === topic}
                                            onChange={handleChange}
                                            className='peer sr-only'
                                        />

                                        <span className='inline-flex rounded-full border-2 border-foreground/15 px-4 py-2 text-sm font-semibold transition-colors peer-checked:border-foreground peer-checked:bg-foreground peer-checked:text-background peer-focus-visible:ring-2 peer-focus-visible:ring-secondary peer-focus-visible:ring-offset-2 hover:border-foreground/40'>
                                            {topic}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </fieldset>

                        <div>
                            <label htmlFor='contact-message' className='mb-2 block font-mono text-xs font-bold uppercase tracking-[0.14em]'>
                                Your message
                            </label>

                            <textarea
                                id='contact-message'
                                name='message'
                                rows={5}
                                value={values.message}
                                onChange={handleChange}
                                maxLength={CONTACT_LIMITS.messageMax}
                                aria-invalid={Boolean(errors.message)}
                                aria-describedby={errors.message ? 'contact-message-error' : undefined}
                                className={cn(inputClass(Boolean(errors.message)), 'resize-y')}
                            />

                            {errors.message && (
                                <p id='contact-message-error' className='mt-2 text-sm font-medium text-destructive'>
                                    {errors.message}
                                </p>
                            )}
                        </div>

                        <div aria-hidden='true' className='absolute -left-[9999px] top-auto size-px overflow-hidden'>
                            <label htmlFor='contact-company'>Company</label>
                            <input
                                id='contact-company'
                                name='company'
                                type='text'
                                tabIndex={-1}
                                autoComplete='off'
                                value={values.company}
                                onChange={handleChange}
                            />
                        </div>

                        <div aria-live='polite'>
                            {serverError && (
                                <p id='contact-server-error' className='rounded-2xl bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive'>
                                    {serverError}
                                </p>
                            )}
                        </div>

                        <div className='flex flex-wrap items-center justify-between gap-4'>
                            <p className='max-w-56 text-sm text-muted-foreground'>
                                Your details are only used to reply to you.
                            </p>

                            <Magnetic>
                                <button
                                    type='submit'
                                    disabled={status === 'sending'}
                                    className='group inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-4 font-mono text-xs font-bold uppercase tracking-[0.14em] text-background transition-colors duration-300 hover:bg-secondary disabled:cursor-wait disabled:opacity-70'
                                >
                                    {status === 'sending' ? (
                                        <>
                                            <LoaderCircle aria-hidden='true' className='size-4 animate-spin' />
                                            Sending
                                        </>
                                    ) : (
                                        <>
                                            Send message
                                            <Send aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                                        </>
                                    )}
                                </button>
                            </Magnetic>
                        </div>
                    </motion.form>
                )}
            </AnimatePresence>
        </div>
    )
}

